import React, { useEffect, useState, useCallback } from 'react';
import { reportAPI } from '../services/api';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, Legend,
} from 'recharts';

/* ─── helpers ─────────────────────────────────────────────── */
const fmt = (n) => n != null ? new Intl.NumberFormat('vi-VN').format(Number(n)) : '—';
const fmtM = (n) => n ? `${(Number(n) / 1e6).toFixed(1)}M` : '—';

const DOW = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];

const LEAVE_LABEL = {
  annual: 'Năm', sick: 'Ốm', maternity: 'Thai sản',
  paternity: 'Thai sản NV', marriage: 'Kết hôn',
  funeral: 'Tang', unpaid: 'KHL', compensatory: 'Bù',
};
const LEAVE_COLORS = ['#6366f1','#0891b2','#22c55e','#f59e0b','#ef4444','#8b5cf6','#64748b','#0d9488'];
const STATUS_COLOR = { approved: '#22c55e', pending: '#f59e0b', rejected: '#ef4444' };
const STATUS_LABEL = { approved: 'Đã duyệt', pending: 'Chờ duyệt', rejected: 'Từ chối' };

function isoWeekStr(dateStr) {
  const d = new Date(dateStr);
  const day = d.getDay() || 7;
  d.setDate(d.getDate() + 4 - day);
  const y = d.getFullYear();
  const jan1 = new Date(y, 0, 1);
  const w = Math.ceil(((d - jan1) / 86400000 + jan1.getDay() + 1) / 7);
  return `${y}-W${String(w).padStart(2, '0')}`;
}

function weekFromISO(isoWeek) {
  // returns Monday date string for ISO week like "2026-W19"
  const [y, w] = isoWeek.split('-W').map(Number);
  const jan4 = new Date(y, 0, 4);
  const day1 = jan4.getDay() || 7;
  const mon = new Date(jan4);
  mon.setDate(jan4.getDate() - day1 + 1 + (w - 1) * 7);
  return mon.toISOString().slice(0, 10);
}

function dayLabel(dateStr) {
  const d = new Date(dateStr);
  return DOW[d.getDay() === 0 ? 6 : d.getDay() - 1] + ' ' + d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' });
}

/* ─── sub-components ──────────────────────────────────────── */
function Section({ title, children, action }) {
  return (
    <div style={{ background: '#fff', borderRadius: 14, padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#1e293b' }}>{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function SummaryCard({ icon, label, value, sub, color }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: '14px 18px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderLeft: `4px solid ${color}`,
      flex: 1, minWidth: 130,
    }}>
      <div style={{ fontSize: 22 }}>{icon}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color: '#1e293b', margin: '4px 0 1px' }}>{value}</div>
      <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color, marginTop: 2, fontWeight: 600 }}>{sub}</div>}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   WEEKLY REPORT
══════════════════════════════════════════════════════════════ */
function WeeklyReport() {
  const [week, setWeek] = useState(isoWeekStr(new Date().toISOString().slice(0, 10)));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback((w) => {
    setLoading(true);
    const dateStr = weekFromISO(w);
    reportAPI.weekly(dateStr)
      .then(r => setData(r.data.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(week); }, [week, load]);

  const s = data?.summary || {};

  // Merge att + task by day into one chart dataset
  const attMap = {};
  (data?.att_by_day || []).forEach(r => { attMap[r.work_date] = r; });
  const taskMap = {};
  (data?.task_by_day || []).forEach(r => { taskMap[r.work_date] = r; });

  const allDates = Array.from(new Set([
    ...Object.keys(attMap),
    ...Object.keys(taskMap),
  ])).sort();

  const attChartData = allDates.map(d => ({
    day: dayLabel(d),
    'Đúng giờ': Number(attMap[d]?.present || 0),
    'Đi muộn': Number(attMap[d]?.late || 0),
    'Vắng': Number(attMap[d]?.absent || 0),
  }));

  const taskChartData = allDates.map(d => {
    const total = Number(taskMap[d]?.total || 0);
    const done  = Number(taskMap[d]?.done  || 0);
    return {
      day: dayLabel(d),
      'Hoàn thành': done,
      'Chưa xong': Math.max(0, total - done),
      pct: total > 0 ? Math.round(done / total * 100) : 0,
    };
  });

  // Leave table grouped by leave_request (each row is one request)
  const leaveRows = data?.leave_overlap || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Week picker */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={() => {
          const mon = new Date(weekFromISO(week));
          mon.setDate(mon.getDate() - 7);
          setWeek(isoWeekStr(mon.toISOString().slice(0, 10)));
        }} style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', fontSize: 18 }}>‹</button>

        <input type="week" value={week} onChange={e => setWeek(e.target.value)}
          style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, fontFamily: 'inherit', cursor: 'pointer' }} />

        <button onClick={() => {
          const mon = new Date(weekFromISO(week));
          mon.setDate(mon.getDate() + 7);
          setWeek(isoWeekStr(mon.toISOString().slice(0, 10)));
        }} style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', fontSize: 18 }}>›</button>

        {data && (
          <span style={{ fontSize: 13, color: '#64748b' }}>
            {new Date(data.week_start).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' })}
            {' — '}
            {new Date(data.week_end).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' })}
          </span>
        )}

        {loading && <span style={{ fontSize: 12, color: '#94a3b8' }}>Đang tải...</span>}
      </div>

      {/* Summary cards */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <SummaryCard icon="✅" label="Ngày công đúng giờ" value={s.present || 0}   color="#22c55e" />
        <SummaryCard icon="⏰" label="Lượt đi muộn"       value={s.late    || 0}   color="#f59e0b"
          sub={s.late > 0 ? `${data?.top_late?.length || 0} nhân viên` : undefined} />
        <SummaryCard icon="🚫" label="Lượt vắng mặt"      value={s.absent  || 0}   color="#ef4444" />
        <SummaryCard icon="📋" label="Nhiệm vụ hoàn thành"
          value={`${s.task_done || 0}/${s.task_total || 0}`}
          sub={`${s.task_pct || 0}%`}
          color={s.task_pct === 100 ? '#16a34a' : s.task_pct >= 50 ? '#d97706' : '#6366f1'} />
        <SummaryCard icon="📅" label="Đơn nghỉ trong tuần" value={s.leave_count || 0} color="#8b5cf6" />
      </div>

      {/* Charts row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

        <Section title="⏱️ Chấm công theo ngày">
          {attChartData.length === 0
            ? <p style={{ color: '#94a3b8', fontSize: 13 }}>Không có dữ liệu chấm công tuần này</p>
            : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={attChartData} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                  <XAxis dataKey="day" fontSize={11} tick={{ fill: '#64748b' }} />
                  <YAxis fontSize={11} tick={{ fill: '#64748b' }} allowDecimals={false} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="Đúng giờ" fill="#22c55e" stackId="a" radius={[0,0,0,0]} />
                  <Bar dataKey="Đi muộn"  fill="#f59e0b" stackId="a" />
                  <Bar dataKey="Vắng"     fill="#ef4444" stackId="a" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            )
          }
        </Section>

        <Section title="📋 Hoàn thành nhiệm vụ theo ngày">
          {taskChartData.length === 0
            ? <p style={{ color: '#94a3b8', fontSize: 13 }}>Không có dữ liệu nhiệm vụ tuần này</p>
            : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={taskChartData} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                  <XAxis dataKey="day" fontSize={11} tick={{ fill: '#64748b' }} />
                  <YAxis fontSize={11} tick={{ fill: '#64748b' }} allowDecimals={false} />
                  <Tooltip formatter={(v, name, props) =>
                    name === 'pct' ? [`${v}%`, 'Tỉ lệ'] : [v, name]
                  } />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="Hoàn thành" fill="#22c55e" stackId="a" />
                  <Bar dataKey="Chưa xong"  fill="#f1f5f9" stackId="a" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            )
          }
        </Section>
      </div>

      {/* Đi muộn nhất */}
      {(data?.top_late || []).length > 0 && (
        <Section title="⚠️ Nhân viên đi muộn nhiều nhất tuần">
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {data.top_late.map((e, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 16px', borderRadius: 10,
                background: i === 0 ? '#fff7ed' : '#f8fafc',
                border: `1px solid ${i === 0 ? '#fed7aa' : '#f1f5f9'}`,
                flex: 1, minWidth: 180,
              }}>
                <div style={{
                  width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                  background: i === 0 ? '#f97316' : '#6366f1',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontWeight: 700, fontSize: 13,
                }}>
                  {e.name?.charAt(0)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{e.name}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>{e.department}</div>
                </div>
                <span style={{
                  fontSize: 20, fontWeight: 800,
                  color: i === 0 ? '#ea580c' : '#64748b',
                }}>
                  {e.late_days}
                  <span style={{ fontSize: 10, fontWeight: 400, marginLeft: 2 }}>lần</span>
                </span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Leave overlap table */}
      <Section title={`📅 Đơn nghỉ phép trong tuần (${leaveRows.length})`}>
        {leaveRows.length === 0
          ? <p style={{ color: '#94a3b8', fontSize: 13 }}>Không có đơn nghỉ nào trong tuần này</p>
          : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['Nhân viên', 'Phòng ban', 'Loại nghỉ', 'Từ ngày', 'Đến ngày', 'Số ngày', 'Trạng thái'].map(h => (
                      <th key={h} style={{ padding: '9px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', whiteSpace: 'nowrap', borderBottom: '2px solid #f1f5f9' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {leaveRows.map((r, i) => (
                    <tr key={i} onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <td style={{ padding: '9px 12px', fontWeight: 600, borderBottom: '1px solid #f8fafc' }}>{r.employee_name}</td>
                      <td style={{ padding: '9px 12px', color: '#64748b', borderBottom: '1px solid #f8fafc' }}>{r.department_name}</td>
                      <td style={{ padding: '9px 12px', borderBottom: '1px solid #f8fafc' }}>
                        <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                          {LEAVE_LABEL[r.leave_type] || r.leave_type}
                        </span>
                      </td>
                      <td style={{ padding: '9px 12px', whiteSpace: 'nowrap', borderBottom: '1px solid #f8fafc' }}>
                        {new Date(r.from_date).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' })}
                      </td>
                      <td style={{ padding: '9px 12px', whiteSpace: 'nowrap', borderBottom: '1px solid #f8fafc' }}>
                        {new Date(r.to_date).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' })}
                      </td>
                      <td style={{ padding: '9px 12px', textAlign: 'center', fontWeight: 700, borderBottom: '1px solid #f8fafc' }}>{r.days}n</td>
                      <td style={{ padding: '9px 12px', borderBottom: '1px solid #f8fafc' }}>
                        <span style={{
                          padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                          color: STATUS_COLOR[r.status] || '#64748b',
                          background: r.status === 'approved' ? '#dcfce7' : r.status === 'pending' ? '#fef9c3' : '#fee2e2',
                        }}>
                          {STATUS_LABEL[r.status] || r.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </Section>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   MONTHLY REPORT
══════════════════════════════════════════════════════════════ */
function MonthlyReport() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    reportAPI.monthly(month)
      .then(r => setData(r.data.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [month]);

  const s = data?.summary || {};
  const payroll = s.payroll;

  // Leave chart data — group by type with approved days
  const leaveTypeMap = {};
  (data?.leave_by_type || []).forEach(r => {
    if (!leaveTypeMap[r.leave_type]) leaveTypeMap[r.leave_type] = { name: LEAVE_LABEL[r.leave_type] || r.leave_type, approved: 0, pending: 0, rejected: 0 };
    leaveTypeMap[r.leave_type][r.status] = Number(r.total_days || 0);
  });
  const leaveChartData = Object.values(leaveTypeMap);

  // Attendance weekly trend
  const weekChartData = (data?.att_by_week || []).map((r, i) => ({
    week: `T${i + 1}`,
    'Đúng giờ': Number(r.present || 0),
    'Đi muộn':  Number(r.late    || 0),
    'Vắng':     Number(r.absent  || 0),
  }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Month picker */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={() => {
          const [y, m] = month.split('-').map(Number);
          const prev = m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
          setMonth(prev);
        }} style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', fontSize: 18 }}>‹</button>

        <input type="month" value={month} onChange={e => setMonth(e.target.value)}
          style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, fontFamily: 'inherit', cursor: 'pointer' }} />

        <button onClick={() => {
          const [y, m] = month.split('-').map(Number);
          const next = m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
          setMonth(next);
        }} style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', fontSize: 18 }}>›</button>

        {loading && <span style={{ fontSize: 12, color: '#94a3b8' }}>Đang tải...</span>}
      </div>

      {/* Summary cards */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <SummaryCard icon="✅" label="Tổng ngày công đúng giờ" value={fmt(s.present)} color="#22c55e" />
        <SummaryCard icon="⏰" label="Tổng lượt đi muộn"       value={fmt(s.late)}    color="#f59e0b" />
        <SummaryCard icon="🚫" label="Tổng lượt vắng mặt"      value={fmt(s.absent)}  color="#ef4444" />
        <SummaryCard icon="📅" label="Lượt nghỉ phép"
          value={fmt(s.leave_count)}
          sub={`${fmt(s.leave_days)} ngày nghỉ`}
          color="#8b5cf6" />
        <SummaryCard icon="🆕" label="Nhân viên mới"   value={s.new_hires || 0} color="#0891b2" />
        <SummaryCard icon="💰" label="Tổng quỹ lương"
          value={payroll ? fmtM(payroll.total_net) : '—'}
          sub={payroll ? `${payroll.employee_count} NV · ${payroll.status === 'approved' ? '✅ Duyệt' : '⏳ Chờ'}` : 'Chưa tính lương'}
          color="#22c55e" />
      </div>

      {/* Charts row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 16 }}>

        <Section title="📈 Xu hướng chấm công theo tuần">
          {weekChartData.length === 0
            ? <p style={{ color: '#94a3b8', fontSize: 13 }}>Không có dữ liệu chấm công tháng này</p>
            : (
              <ResponsiveContainer width="100%" height={210}>
                <LineChart data={weekChartData} margin={{ top: 4, right: 12, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="week" fontSize={11} tick={{ fill: '#64748b' }} />
                  <YAxis fontSize={11} tick={{ fill: '#64748b' }} allowDecimals={false} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="Đúng giờ" stroke="#22c55e" strokeWidth={2} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="Đi muộn"  stroke="#f59e0b" strokeWidth={2} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="Vắng"     stroke="#ef4444" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            )
          }
        </Section>

        <Section title="📅 Nghỉ phép theo loại">
          {leaveChartData.length === 0
            ? <p style={{ color: '#94a3b8', fontSize: 13 }}>Không có đơn nghỉ tháng này</p>
            : (
              <ResponsiveContainer width="100%" height={210}>
                <BarChart data={leaveChartData} layout="vertical" margin={{ top: 4, right: 20, left: 20, bottom: 0 }}>
                  <XAxis type="number" fontSize={11} tick={{ fill: '#64748b' }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" fontSize={11} tick={{ fill: '#64748b' }} width={70} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="approved" name="Đã duyệt" fill="#22c55e" stackId="a" radius={[0,0,0,0]} />
                  <Bar dataKey="pending"  name="Chờ duyệt" fill="#f59e0b" stackId="a" />
                  <Bar dataKey="rejected" name="Từ chối"  fill="#ef4444" stackId="a" radius={[0,4,4,0]} />
                </BarChart>
              </ResponsiveContainer>
            )
          }
        </Section>
      </div>

      {/* Attendance per employee */}
      <Section title="👥 Chi tiết chấm công nhân viên">
        {(data?.att_by_employee || []).length === 0
          ? <p style={{ color: '#94a3b8', fontSize: 13 }}>Không có dữ liệu</p>
          : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['Nhân viên', 'Phòng ban', 'Đúng giờ', 'Đi muộn', 'Vắng', 'Đã chấm', 'Tỉ lệ đúng giờ'].map(h => (
                      <th key={h} style={{ padding: '9px 12px', textAlign: h === 'Nhân viên' || h === 'Phòng ban' ? 'left' : 'center', fontSize: 11, fontWeight: 700, color: '#64748b', whiteSpace: 'nowrap', borderBottom: '2px solid #f1f5f9' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.att_by_employee.map((e, i) => {
                    const total   = Number(e.checked_in || 0);
                    const present = Number(e.present || 0);
                    const late    = Number(e.late || 0);
                    const absent  = Number(e.absent || 0);
                    const pct     = total > 0 ? Math.round(present / total * 100) : 0;
                    return (
                      <tr key={i} onMouseEnter={ev => ev.currentTarget.style.background = '#fafafa'}
                        onMouseLeave={ev => ev.currentTarget.style.background = ''}>
                        <td style={{ padding: '9px 12px', fontWeight: 600, borderBottom: '1px solid #f8fafc' }}>{e.name}</td>
                        <td style={{ padding: '9px 12px', color: '#64748b', borderBottom: '1px solid #f8fafc' }}>{e.department}</td>
                        <td style={{ padding: '9px 12px', textAlign: 'center', fontWeight: 700, color: '#22c55e', borderBottom: '1px solid #f8fafc' }}>{present}</td>
                        <td style={{ padding: '9px 12px', textAlign: 'center', fontWeight: 700, color: late > 0 ? '#f59e0b' : '#94a3b8', borderBottom: '1px solid #f8fafc' }}>{late}</td>
                        <td style={{ padding: '9px 12px', textAlign: 'center', fontWeight: 700, color: absent > 0 ? '#ef4444' : '#94a3b8', borderBottom: '1px solid #f8fafc' }}>{absent}</td>
                        <td style={{ padding: '9px 12px', textAlign: 'center', borderBottom: '1px solid #f8fafc' }}>{total}</td>
                        <td style={{ padding: '9px 12px', borderBottom: '1px solid #f8fafc' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ flex: 1, background: '#f1f5f9', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                              <div style={{ width: `${pct}%`, height: '100%', background: pct >= 80 ? '#22c55e' : pct >= 60 ? '#f59e0b' : '#ef4444', borderRadius: 4 }} />
                            </div>
                            <span style={{ fontSize: 11, fontWeight: 700, color: pct >= 80 ? '#16a34a' : pct >= 60 ? '#d97706' : '#dc2626', flexShrink: 0 }}>
                              {pct}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        }
      </Section>

      {/* Task completion per employee */}
      {(data?.task_by_employee || []).length > 0 && (
        <Section title="📋 Hoàn thành nhiệm vụ theo nhân viên">
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Nhân viên', 'Phòng ban', 'Tổng', 'Hoàn thành', 'Bỏ qua', 'Chưa làm', 'Tỉ lệ'].map(h => (
                    <th key={h} style={{ padding: '9px 12px', textAlign: h === 'Nhân viên' || h === 'Phòng ban' ? 'left' : 'center', fontSize: 11, fontWeight: 700, color: '#64748b', whiteSpace: 'nowrap', borderBottom: '2px solid #f1f5f9' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.task_by_employee.map((e, i) => {
                  const total   = Number(e.total   || 0);
                  const done    = Number(e.done    || 0);
                  const skipped = Number(e.skipped || 0);
                  const pending = Number(e.pending || 0);
                  const pct     = total > 0 ? Math.round(done / total * 100) : 0;
                  return (
                    <tr key={i} onMouseEnter={ev => ev.currentTarget.style.background = '#fafafa'}
                      onMouseLeave={ev => ev.currentTarget.style.background = ''}>
                      <td style={{ padding: '9px 12px', fontWeight: 600, borderBottom: '1px solid #f8fafc' }}>{e.name}</td>
                      <td style={{ padding: '9px 12px', color: '#64748b', borderBottom: '1px solid #f8fafc' }}>{e.department}</td>
                      <td style={{ padding: '9px 12px', textAlign: 'center', borderBottom: '1px solid #f8fafc' }}>{total}</td>
                      <td style={{ padding: '9px 12px', textAlign: 'center', fontWeight: 700, color: '#22c55e', borderBottom: '1px solid #f8fafc' }}>{done}</td>
                      <td style={{ padding: '9px 12px', textAlign: 'center', color: '#64748b', borderBottom: '1px solid #f8fafc' }}>{skipped}</td>
                      <td style={{ padding: '9px 12px', textAlign: 'center', color: pending > 0 ? '#94a3b8' : '#94a3b8', borderBottom: '1px solid #f8fafc' }}>{pending}</td>
                      <td style={{ padding: '9px 12px', borderBottom: '1px solid #f8fafc' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ flex: 1, background: '#f1f5f9', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                            <div style={{ width: `${pct}%`, height: '100%', background: pct >= 80 ? '#22c55e' : pct >= 50 ? '#d97706' : '#6366f1', borderRadius: 4 }} />
                          </div>
                          <span style={{ fontSize: 11, fontWeight: 700, color: pct >= 80 ? '#16a34a' : pct >= 50 ? '#d97706' : '#6366f1', flexShrink: 0 }}>
                            {pct}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════════════════════════ */
export default function ReportsPage() {
  const [tab, setTab] = useState('weekly');

  const tabs = [
    { key: 'weekly',  label: '📅 Báo cáo tuần' },
    { key: 'monthly', label: '📊 Báo cáo tháng' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      <div>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1e293b', margin: 0 }}>Báo cáo</h1>
        <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: 13 }}>Thống kê chấm công, nghỉ phép và nhiệm vụ theo tuần / tháng</p>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, background: '#f1f5f9', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '8px 22px', borderRadius: 7, border: 'none', cursor: 'pointer',
            fontFamily: 'inherit', fontSize: 13, fontWeight: tab === t.key ? 700 : 400,
            background: tab === t.key ? '#fff' : 'transparent',
            color: tab === t.key ? '#1e3a8a' : '#64748b',
            boxShadow: tab === t.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            transition: 'all 0.15s',
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'weekly'  && <WeeklyReport />}
      {tab === 'monthly' && <MonthlyReport />}
    </div>
  );
}
